"use client";
import dynamic from "next/dynamic";
import Image from "next/image";
import React, { useEffect, useState, useRef } from "react";
import { AchievementsBlock } from "../index";
import { useRequestAnimationFrame } from "@/hooks/useRequestAnimationFrame";
import { useAutoplayUnlock } from "@/hooks/useAutoplayUnlock";

// Dynamic imports
const Cards = dynamic(() => import("./Cards"));
const Section = dynamic(() => import("@/Components/LayoutComponents/Section"), { ssr: true });
import { toWebp } from "@/utils/common";

interface AchievementsProps {
  data?: AchievementsBlock;
}

const isVideoFile = (filename: string): boolean => {
  const ext = filename.toLowerCase().substring(filename.lastIndexOf("."));
  return [".mp4", ".webm", ".ogg", ".mov", ".avi", ".mkv"].includes(ext);
};

const isImageFile = (filename: string): boolean => {
  const ext = filename.toLowerCase().substring(filename.lastIndexOf("."));
  return [".jpg", ".jpeg", ".png", ".gif", ".webp", ".svg", ".bmp"].includes(
    ext,
  );
};

const baseUrl = process.env.NEXT_PUBLIC_REACT_APP_RESOURCE_BASE_URL;

const Achievements: React.FC<AchievementsProps> = ({ data }) => {
  const videoRef = useRef<HTMLVideoElement>(null);

  const [bannerAnimationLink, setLink] = useState("");
  const [cardIndexes, setCardIndexes] = useState<number[]>([]);
  const [blinkingIndex, setBlinkingIndex] = useState<number>(-1);
  const map = data?.image?.data?.attributes?.url || "";

  useEffect(() => {
    setLink(data?.animation_link ?? "");
  }, [data?.animation_link]);

  // Safari autoplay fix
  useEffect(() => {
    const video = videoRef.current;

    if (video) {
      video.muted = true;
      const playPromise = video.play();

      if (playPromise !== undefined) {
        playPromise.catch(() => {});
      }
    }
  }, [bannerAnimationLink]);

  useAutoplayUnlock(videoRef, [bannerAnimationLink]);

  useEffect(() => {
    if (data?.achievementCard) {
      setCardIndexes(
        data.achievementCard.map(
          (achievement, index: number) => achievement.id ?? index,
        ),
      );
    }
  }, [data?.achievementCard]);

  // Initialize first card
  useEffect(() => {
    if (cardIndexes.length === 0) return;
    setBlinkingIndex((prev) => {
      if (prev !== -1) return prev;
      return cardIndexes[0];
    });
  }, [cardIndexes]);

  // Animation frame blinking logic
  useRequestAnimationFrame(
    () => {
      setBlinkingIndex((prev) => {
        let next = prev;
        if (cardIndexes.length === 1) return cardIndexes[0];
        while (next === prev) {
          next = cardIndexes[Math.floor(Math.random() * cardIndexes.length)];
        }
        return next;
      });
    },
    3000,
    cardIndexes.length > 0,
  );

  return (
    <Section className="bg-background-dark relative overflow-hidden">
      <div className="absolute bottom-0 right-0 h-full w-full z-20">
        <Image
          src="/images/Achivements.png"
          alt="Achievements"
          fill
          style={{ objectFit: "cover", objectPosition: "center", aspectRatio: "16 / 9" }}
          loading="lazy"
          quality={75}
          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
        />
      </div>

      <div className="container relative z-10">
        <div className="max-w-[927px]">
          <h2 className="text-heading-1 text-primary-text-white font-bold capitalize md:text-start text-center">
            {data?.title}
          </h2>
          <p className="text-subtitle-2 font-regular mt-3 text-secondary-text-white md:text-start text-center">
            {data?.sub_title}
          </p>
        </div>

        <div className="mt-10 grid xl:grid-cols-4 md:grid-cols-3 grid-cols-2 -space-x-[1px] -space-y-[1px]" style={{ willChange: "contents" }}>
          <div className="row-span-10 row-start-2 xl:block hidden"></div>
          <div className="xl:col-span-2 md:col-span-3 col-span-2 lg:max-h-[415px] xl:mb-0 mb-8">
            {bannerAnimationLink ? (
              <video
                ref={videoRef}
                src={bannerAnimationLink}
                width={613}
                height={302}
                style={{ aspectRatio: "613 / 302" }}
                className="w-full h-full object-cover object-center p-1"
                autoPlay
                muted
                loop
                playsInline
                disablePictureInPicture
                disableRemotePlayback
                preload="metadata"
                poster="/images/poster/achievements-poster.webp"
              />
            ) : map && isVideoFile(map) ? (
              <video
                ref={videoRef}
                src={`${baseUrl}${data?.image?.data?.attributes?.url}`}
                width={613}
                height={302}
                style={{ aspectRatio: "613 / 302" }}
                className="w-full h-full object-cover object-center p-1"
                autoPlay
                muted
                loop
                playsInline
                disablePictureInPicture
                disableRemotePlayback
                preload="metadata"
                poster="/images/poster/achievements-poster.webp"
              />
            ) : map && isImageFile(map) ? (
              <Image
                src={toWebp(`${baseUrl}${data?.image?.data?.attributes?.url}`)}
                width={613}
                height={302}
                alt="map"
                quality={80}
                style={{ aspectRatio: "613 / 302" }}
                className="w-full h-full object-cover object-center p-1"
              />
            ) : null}
          </div>
          <div className="block xl:hidden"></div>

          {Array.isArray(data?.achievementCard) &&
            data.achievementCard.length > 0 ? (
            data.achievementCard.map((achievement, index: number) => {
              const hasContent =
                achievement.scale_up?.trim() && achievement.description?.trim();

              if (!hasContent) {
                // Show fallback image if content is missing
                return (
                  <div
                    key={achievement.id ?? index + 1}
                    className="h-full overflow-hidden"
                  >
                    <Image
                      src={toWebp(
                        `${baseUrl}${achievement.image?.data?.attributes?.url}`,
                      )}
                      width={613}
                      height={302}
                      alt="achievement fallback"
                      className="home-achievements-image w-full h-full object-cover object-center lg:aspect-square aspect-[191_/_179] transition-all duration-500 scale-[1] hover:scale-[1.1]"
                      quality={80}
                    />
                  </div>
                );
              }

              // Show Cards if content is available
              return (
                <div key={achievement.id ?? achievement.scale_up}>
                  <Cards
                    // key={achievement.id ?? achievement.scale_up}
                    counter={achievement.scale_up}
                    details={achievement.description}
                    className={`xl:col-start-[unset] col-start-2 ${blinkingIndex === achievement.id
                      ? "after:opacity-100 before:opacity-100 bg-blue-50 text-primary-text"
                      : "text-primary-text-white"
                      }`}
                    classNameDot={`transition-all ease-in-out ${blinkingIndex === achievement.id
                      ? "after:opacity-100 before:opacity-100 bg-blue-50 text-primary-text opacity-100"
                      : "opacity-0"
                      }`}
                  />
                </div>
              );
            })
          ) : (
            <p className="text-center text-gray-400">
              No achievements to display.
            </p>
          )}
        </div>
      </div>
    </Section>
  );
};
export default React.memo(Achievements);
